import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { DateTime } from 'luxon';
import { Permissions } from '../../../db/permissions.js';
import { checkPermission } from '../../permissions/helpers.js';
import { createPublishment } from '../../publishment/api/createPublishment.js';
import { getPublishment } from '../../publishment/api/getPublishment.js';
import { Draft, DraftStatusScheduledDeploy } from '../db/Draft.js';

// publish means to override the original entity
export const publishDraft = registerQuery({
  type: () => String,
  operationName: 'publishDraft',
  resolve: (draftID: string, publish: boolean, scheduledAt?: DateTime) => {
    return async () => {
      // check permissions
      //______________________________________________________________________________________________
      await checkPermission({ context, permission: Permissions.DesignerPermission.EDIT });

      // 1. use getEntityByID to make use of publishfilter
      const rawDraftCollection = await getRawCollection<Draft>(Draft._collectionName);
      const rawDraft = await rawDraftCollection.findOne<Draft>({ _id: draftID });
      if (!rawDraft) throw new Error(`no draft with id '${draftID}' found.`);

      if (scheduledAt) {
        await rawDraftCollection.updateOne(
          { _id: draftID },
          {
            $set: {
              status: new DraftStatusScheduledDeploy({
                name: 'SCHEDULED_DEPLOY',
                deployAt: scheduledAt.toJSDate(),
                publish,
              }).toObject() as any as DraftStatusScheduledDeploy,
            },
          },
        );
        return;
      }

      const resolvedEntityNode = baseqlMetaData.entities.resolveNode(rawDraft.entity._class);
      const EntityClass = resolvedEntityNode.type() as typeof MongoEntity;

      // 3. check if we have access/permission for the target entity. Publishfilter is used here
      const entity = await getByID(EntityClass, rawDraft.referenceID)({ _id: 1 });
      if (!entity) throw new Error(`no permission for target entity.`);

      // update processing
      //______________________________________________________________________________________________

      // 1. update target
      const rawEntityCollection = await getRawCollection<MongoEntity>(EntityClass._collectionName);
      // make to not override the _id field
      await rawEntityCollection.updateOne(
        { _id: entity._id },
        {
          $set: {
            ...rawDraft.entity,
            // make sure to retain _id
            _id: entity._id,
          },
        },
      );
      // 2. delete draft
      await rawDraftCollection.deleteOne({ _id: draftID });

      // 3. publish if needed
      if (publish) {
        const existingPublishment = await getPublishment(entity._id)({ _id: 1 });
        if (!existingPublishment) await createPublishment(entity._id)({ _id: 1 });
      }
    };
  },
  params: [
    { name: 'draftID', type: () => String },
    { name: 'publish', type: () => Boolean },
    { name: 'scheduledAt', type: () => DateTime },
  ],
});
